/**
 * Form definition picker (Autocomplete over active org form definitions).
 *
 * - `value` / `onChange` use form definition **key** (not GraphQL id).
 * - Search matches translated name and key (case-insensitive).
 * - `onObjectTypeChange` runs when a definition is selected, not when cleared.
 * - Pass `formDefinitions` to skip the internal query (multi-row UIs).
 * - `excludeKeys` hides keys from other rows; `loading` when options are parent-fetched.
 *
 * Behavioral contract: see formDefinitionSelect.test.tsx.
 */

import { useQuery } from '@apollo/client';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { UserContext } from '@providers/userContextProvider';
import { kebabCase } from 'lodash-es';
import { type HTMLAttributes, useCallback, useContext, useMemo } from 'react';
import { useLocalization } from '../../../hooks/useLocalization';
import { FETCH_FORM_DEFINITIONS } from './formDefinitionSelect.query';
import {
  buildFetchFormDefinitionsVariables,
  type FormDefinitionOption,
} from './formDefinitionSelect.util';

export type { FormDefinitionOption };
export { FETCH_FORM_DEFINITIONS };

const getFormDefinitionLabel = (
  formDefinition: FormDefinitionOption,
  translateBackendString: (value: unknown) => string,
  showValue?: boolean,
) => {
  const name = translateBackendString(formDefinition.translatedName);
  if (showValue) {
    return `${name} (${formDefinition.key})`;
  }
  return name;
};

type FormDefinitionSelectProp = {
  label: string;
  onChange: (key: string) => void;
  value: string | undefined;
  sx?: any;
  size?: 'small' | 'medium';
  showValue?: boolean;
  filter?: any;
  onBlur?: any;
  error?: any;
  errorText?: string;
  testId?: string;
  organizationId?: string;
  onObjectTypeChange?: (objectType: string) => void;
  formDefinitions?: FormDefinitionOption[];
  excludeKeys?: string[];
  loading?: boolean;
};

export const FormDefinitionSelect = ({
  label,
  onChange,
  value,
  sx = {},
  size,
  showValue,
  filter,
  onBlur,
  error,
  errorText,
  testId,
  organizationId,
  onObjectTypeChange,
  formDefinitions: injectedFormDefinitions,
  excludeKeys = [],
  loading: loadingProp,
}: FormDefinitionSelectProp) => {
  const { translateBackendString } = useLocalization();
  const { activeOrganization } = useContext(UserContext);
  const resolvedOrganizationId = organizationId ?? activeOrganization?.id;

  const formatOptionLabel = useCallback(
    (formDefinition: FormDefinitionOption) =>
      getFormDefinitionLabel(formDefinition, translateBackendString, showValue),
    [showValue, translateBackendString],
  );

  // undefined = self-fetch; [] = injected (loading/empty) and must not N× query in multi-row UIs.
  const skipQuery =
    injectedFormDefinitions !== undefined || !resolvedOrganizationId;

  const { data, loading: queryLoading } = useQuery(FETCH_FORM_DEFINITIONS, {
    variables: buildFetchFormDefinitionsVariables(
      resolvedOrganizationId!,
      filter,
    ),
    fetchPolicy: 'cache-and-network',
    skip: skipQuery,
  });

  const loading =
    loadingProp !== undefined ? loadingProp : skipQuery ? false : queryLoading; // parent vs Apollo

  const queriedFormDefinitions: FormDefinitionOption[] = useMemo(
    () => data?.fetchFormDefinitions?.formDefinitions ?? [],
    [data],
  );

  const formDefinitions =
    injectedFormDefinitions !== undefined
      ? injectedFormDefinitions
      : queriedFormDefinitions;

  const excludeKeySet = useMemo(
    () => new Set(excludeKeys.map((key) => key.trim()).filter(Boolean)),
    [excludeKeys],
  );

  // Dropdown excludes other rows' keys; selected value still resolves from full list for this row.
  const selectableFormDefinitions = useMemo(
    () =>
      formDefinitions.filter(
        (formDefinition) => !excludeKeySet.has(formDefinition.key),
      ),
    [formDefinitions, excludeKeySet],
  );

  const selectedOption = useMemo(() => {
    if (!value) {
      return null;
    }
    // Config may store keys with surrounding whitespace before normalize on save.
    const trimmedValue = value.trim();
    return (
      formDefinitions.find(
        (fd) => fd.key === value || fd.key === trimmedValue,
      ) ?? null
    );
  }, [formDefinitions, value]);

  return (
    <Autocomplete
      label={label}
      fullWidth
      sx={sx}
      size={size}
      options={selectableFormDefinitions}
      value={selectedOption}
      onBlur={onBlur}
      error={error}
      errorText={errorText}
      loading={loading}
      testId={testId ?? 'formDefinition-select'}
      getOptionLabel={formatOptionLabel}
      isOptionEqualToValue={(
        option: FormDefinitionOption,
        selected: FormDefinitionOption,
      ) => option.key === selected.key}
      filterOptions={(options: FormDefinitionOption[], state: object) => {
        const query = (state as { inputValue: string }).inputValue
          .trim()
          .toLowerCase();
        if (!query) {
          return options;
        }
        return options.filter((formDefinition) => {
          const labelText = formatOptionLabel(formDefinition).toLowerCase();
          return (
            labelText.includes(query) ||
            formDefinition.key.toLowerCase().includes(query)
          );
        });
      }}
      onChange={(_event: unknown, newValue: FormDefinitionOption | null) => {
        const key = newValue?.key ?? '';
        onChange(key);
        if (onObjectTypeChange && newValue?.objectType) {
          onObjectTypeChange(newValue.objectType);
        }
      }}
      renderOption={(
        props: HTMLAttributes<HTMLLIElement>,
        option: FormDefinitionOption,
      ) => (
        <li
          {...props}
          key={option.key}
          data-testid={
            testId
              ? `${testId}-${kebabCase(option.key)}`
              : `formDefinition-select-${kebabCase(option.key)}`
          }
        >
          {formatOptionLabel(option)}
        </li>
      )}
    />
  );
};
